import React, { Suspense, useMemo } from "react";
import * as THREE from "three";
import { useGLTF, Clone } from "@react-three/drei";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { usePhotoMaps, assets } from "./rigAssets";
import { PALETTE, FOUNDATION_H, BASE_H, CAP_H, GROUND_SPEC, groundTexture, fenceMeshTexture, FENCE_TILE, metricPlane, merge, box, cyl } from "./rigMaterials";
import { rect, dims, floorSpan } from "./rigGeometry";

/**
 * Site dressing around the rig (Riggdesign 3D, level 2): a paved/gravelled rig pad, a welded-mesh site fence with an
 * open gate, scale references (service van in a marked bay, two workers in hi-vis) and joint flashings between
 * adjacent modules. Everything is procedural and merged into a handful of draw calls; the pad follows the rig's
 * bounding box so the surroundings grow with the design.
 */

export const PAD_MARGIN = 6; // metres of pad beyond the rig footprint
export const FENCE_INSET = 1.2; // fence line inside the pad edge
const PANEL_W = 3.5, PANEL_H = 2.0;
export const GATE_W = 6;
const STORD_BLUE = "#2670f2";
export const DRESSING_COLORS = { rubber: "#1e2124", paint: "#f1f3f4", dark: "#2a2e33", hivis: "#ff6a13", stripe: "#dfe3e6", skin: "#d9a887", helmet: "#f4f5f6", mesh: "#cfd4d8", foot: "#5b5e63", line: "#e6e8ea" };
const C = DRESSING_COLORS;

/** Pad rectangle around the rig bounds, quantised to 2 m so it does not rebuild on every drag frame. */
const q2 = (v, up) => (up ? Math.ceil(v / 2) * 2 : Math.floor(v / 2) * 2);
export const padRect = (b) => ({ x0: q2(b.x0 - PAD_MARGIN), x1: q2(b.x1 + PAD_MARGIN, true), z0: q2(b.z0 - PAD_MARGIN), z1: q2(b.z1 + PAD_MARGIN, true) });

/** Horizontal tube along local x, then yawed. */
const hcyl = (r, len, x, y, z, yaw) => { const g = new THREE.CylinderGeometry(r, r, len, 8); g.rotateZ(Math.PI / 2); return { g, p: [x, y, z], r: [0, yaw, 0] }; };
const rbox = (w, h, l, rad, x, y, z, rot) => ({ g: new RoundedBoxGeometry(w, h, l, 3, rad), p: [x, y, z], r: rot });
const sphere = (r, x, y, z) => ({ g: new THREE.SphereGeometry(r, 14, 10), p: [x, y, z] });
/** Yaw a list of local items (x right, z forward) and move them to (x, z). */
export function place(items, x, z, yaw) {
  const c = Math.cos(yaw), s = Math.sin(yaw);
  return items.map((it) => {
    const [lx, ly, lz] = it.p;
    const r = it.r || [0, 0, 0];
    return { ...it, p: [x + lx * c + lz * s, ly, z - lx * s + lz * c], r: [r[0], r[1] + yaw, r[2]] };
  });
}

/* ---------------- rig pad ---------------- */

export function RigPad({ P, kind, faded }) {
  const geo = useMemo(() => {
    const r = 1.5, x0 = P.x0, x1 = P.x1, y0 = -P.z1, y1 = -P.z0; // shape is drawn in (x, −z)
    const s = new THREE.Shape();
    s.moveTo(x0 + r, y0); s.lineTo(x1 - r, y0); s.quadraticCurveTo(x1, y0, x1, y0 + r); s.lineTo(x1, y1 - r); s.quadraticCurveTo(x1, y1, x1 - r, y1);
    s.lineTo(x0 + r, y1); s.quadraticCurveTo(x0, y1, x0, y1 - r); s.lineTo(x0, y0 + r); s.quadraticCurveTo(x0, y0, x0 + r, y0);
    const g = new THREE.ShapeGeometry(s, 6);
    g.rotateX(-Math.PI / 2); // uv = (x, −z) in metres → metric tiling
    return g;
  }, [P.x0, P.x1, P.z0, P.z1]);
  const tex = groundTexture(kind);
  const spec = GROUND_SPEC[kind];
  const photo = usePhotoMaps();
  const pm = photo?.[kind];
  return (
    <mesh geometry={geo} position={[0, 0, 0]} receiveShadow raycast={() => null}>
      {pm ? (
        <meshStandardMaterial map={pm.map} normalMap={pm.normalMap} normalScale={[0.6, 0.6]} roughnessMap={pm.roughnessMap} roughness={pm.roughnessMap ? 1 : spec.roughness} color={kind === "asfalt" ? "#d9dbdd" : "#c4bfb6"} metalness={0} transparent={faded} opacity={faded ? 0.55 : 1} depthWrite={!faded} />
      ) : (
        <meshStandardMaterial map={tex} color="#ffffff" roughness={spec.roughness} metalness={0} transparent={faded} opacity={faded ? 0.55 : 1} depthWrite={!faded} />
      )}
    </mesh>
  );
}

/* ---------------- fence ---------------- */

function fenceParts(P) {
  const F = FENCE_INSET;
  const x0 = P.x0 + F, x1 = P.x1 - F, z0 = P.z0 + F, z1 = P.z1 - F;
  const cx = (x0 + x1) / 2;
  const frames = [], panels = [], feet = [];
  const run = (ax, az, bx, bz, { gate = false } = {}) => {
    const L = Math.hypot(bx - ax, bz - az);
    if (L < 0.4) return;
    const n = gate ? 1 : Math.max(1, Math.round(L / PANEL_W));
    const pw = L / n;
    const ux = (bx - ax) / L, uz = (bz - az) / L;
    const yaw = Math.atan2(-uz, ux);
    const yb = 0.12; // panel frame bottom (on the feet)
    for (let i = 0; i <= n; i++) {
      const px = ax + ux * i * pw, pz = az + uz * i * pw;
      frames.push(cyl(gate ? 0.045 : 0.024, PANEL_H + (gate ? 0.25 : 0.1), px, (PANEL_H + (gate ? 0.25 : 0.1)) / 2 + yb - 0.1, pz));
      if (!gate) feet.push({ g: new THREE.BoxGeometry(0.72, 0.12, 0.22), p: [px, 0.06, pz], r: [0, yaw, 0] });
    }
    for (let i = 0; i < n; i++) {
      const mx = ax + ux * (i + 0.5) * pw, mz = az + uz * (i + 0.5) * pw;
      frames.push(hcyl(0.016, pw - 0.05, mx, yb + PANEL_H - 0.02, mz, yaw), hcyl(0.016, pw - 0.05, mx, yb + 0.03, mz, yaw));
      panels.push({ g: metricPlane(pw - 0.08, PANEL_H - 0.1, FENCE_TILE), p: [mx, yb + PANEL_H / 2, mz], r: [0, yaw, 0] });
    }
  };
  run(x0, z0, x1, z0);
  run(x1, z0, x1, z1);
  run(x0, z1, x0, z0);
  run(x0, z1, cx - GATE_W / 2, z1);
  run(cx + GATE_W / 2, z1, x1, z1);
  // gate leaves swung open outwards (towards the approach side)
  const a = THREE.MathUtils.degToRad(68), half = GATE_W / 2;
  run(cx - half, z1, cx - half + half * Math.cos(a), z1 + half * Math.sin(a), { gate: true });
  run(cx + half, z1, cx + half - half * Math.cos(a), z1 + half * Math.sin(a), { gate: true });
  return { frames: merge(frames), panels: merge(panels), feet: merge(feet) };
}

export function Fence({ P }) {
  const parts = useMemo(() => fenceParts(P), [P.x0, P.x1, P.z0, P.z1]);
  const mesh = fenceMeshTexture();
  return (
    <group raycast={() => null}>
      <mesh geometry={parts.frames} castShadow><meshStandardMaterial color={PALETTE.galv} roughness={0.4} metalness={0.7} /></mesh>
      <mesh geometry={parts.feet} receiveShadow><meshStandardMaterial color={C.foot} roughness={0.9} metalness={0.05} /></mesh>
      <mesh geometry={parts.panels}><meshStandardMaterial color={C.mesh} alphaMap={mesh} transparent depthWrite={false} side={THREE.DoubleSide} roughness={0.45} metalness={0.6} /></mesh>
    </group>
  );
}

/* ---------------- scale references ---------------- */

export function vanItems() {
  // local: width x, forward +z, wheels on the ground
  const paint = [rbox(1.9, 1.35, 4.2, 0.12, 0, 1.125, -0.35), rbox(1.86, 0.62, 1.25, 0.1, 0, 0.78, 2.05), box(0.32, 0.14, 0.04, -0.62, 0.92, 2.68), box(0.32, 0.14, 0.04, 0.62, 0.92, 2.68)];
  const glass = [{ g: new THREE.BoxGeometry(1.66, 0.86, 0.05), p: [0, 1.46, 1.62], r: [-0.42, 0, 0] }, box(0.04, 0.5, 1.0, -0.94, 1.5, 0.95), box(0.04, 0.5, 1.0, 0.94, 1.5, 0.95), box(1.6, 0.6, 0.04, 0, 1.45, -2.46)];
  const rubber = [], hubs = [];
  for (const [x, z] of [[-0.86, 1.5], [0.86, 1.5], [-0.86, -1.5], [0.86, -1.5]]) {
    rubber.push(hcyl(0.34, 0.24, x, 0.34, z, 0));
    hubs.push(hcyl(0.17, 0.26, x, 0.34, z, 0));
  }
  const brand = [box(0.03, 0.14, 3.4, -0.955, 1.05, -0.55), box(0.03, 0.14, 3.4, 0.955, 1.05, -0.55)];
  return { paint, glass, rubber, hubs, brand };
}

export function workerItems() {
  // local: facing +z, ~1.78 m tall
  const dark = [cyl(0.085, 0.82, -0.11, 0.41, 0), cyl(0.085, 0.82, 0.11, 0.41, 0), cyl(0.05, 0.6, -0.28, 1.1, 0), cyl(0.05, 0.6, 0.28, 1.1, 0)];
  const hivis = [rbox(0.44, 0.62, 0.26, 0.06, 0, 1.13, 0)];
  const stripe = [box(0.46, 0.05, 0.28, 0, 1.0, 0), box(0.46, 0.05, 0.28, 0, 1.2, 0)];
  const skin = [sphere(0.11, 0, 1.58, 0), sphere(0.05, -0.28, 0.78, 0), sphere(0.05, 0.28, 0.78, 0)];
  const helmet = [{ g: new THREE.SphereGeometry(0.135, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2), p: [0, 1.62, 0] }, cyl(0.15, 0.02, 0, 1.62, 0)];
  return { dark, hivis, stripe, skin, helmet };
}

export function ScaleAssets({ P, b }) {
  const parts = useMemo(() => {
    const F = FENCE_INSET;
    const z1f = P.z1 - F, cx = (P.x0 + P.x1) / 2;
    const bayD = 2.6, bayL = 6.2, minX = P.x0 + F + 0.6, zc = z1f - 0.35 - bayD / 2;
    // parallel parking bays along the south fence, west of the gate (only those that fit on the pad)
    const lines = [];
    let bays = 0;
    for (let k = 0; k < 2; k++) {
      const xe = cx - 4 - bayL * k, xw = xe - bayL;
      if (xw < minX) break;
      if (k === 0) lines.push(box(0.12, 0.012, bayD, xe, 0.006, zc));
      lines.push(box(0.12, 0.012, bayD, xw, 0.006, zc));
      bays++;
    }
    const van = vanItems();
    const vanX = cx - 4 - bayL / 2;
    const out = { lines: merge(lines) };
    if (bays > 0) for (const k of Object.keys(van)) out[`van_${k}`] = merge(place(van[k], vanX, zc, Math.PI / 2));
    // two workers: one by the van, one at the rig's south-west corner looking at the building
    const w1 = workerItems(), w2 = workerItems();
    const p1 = { x: cx - 3.3, z: z1f - 3.4, yaw: -Math.PI / 2 + 0.3 };
    const p2 = { x: b.x0 - 1.7, z: b.z1 + 1.5 };
    p2.yaw = Math.atan2(b.x0 + 3 - p2.x, b.z0 - p2.z);
    for (const k of Object.keys(w1)) out[`w_${k}`] = merge([...place(w1[k], p1.x, p1.z, p1.yaw), ...place(w2[k], p2.x, p2.z, p2.yaw)]);
    return out;
  }, [P.x0, P.x1, P.z0, P.z1, b.x0, b.z0, b.z1]);
  return (
    <group raycast={() => null}>
      {parts.lines && <mesh geometry={parts.lines}><meshStandardMaterial color={C.line} roughness={0.8} /></mesh>}
      {parts.van_paint && <mesh geometry={parts.van_paint} castShadow><meshStandardMaterial color={C.paint} roughness={0.32} metalness={0.25} /></mesh>}
      {parts.van_glass && <mesh geometry={parts.van_glass}><meshStandardMaterial color={PALETTE.glass} roughness={0.08} metalness={0.6} envMapIntensity={1.4} /></mesh>}
      {parts.van_rubber && <mesh geometry={parts.van_rubber} castShadow><meshStandardMaterial color={C.rubber} roughness={0.92} /></mesh>}
      {parts.van_hubs && <mesh geometry={parts.van_hubs}><meshStandardMaterial color={PALETTE.galv} roughness={0.35} metalness={0.7} /></mesh>}
      {parts.van_brand && <mesh geometry={parts.van_brand}><meshStandardMaterial color={STORD_BLUE} roughness={0.4} metalness={0.2} /></mesh>}
      <mesh geometry={parts.w_dark} castShadow><meshStandardMaterial color={C.dark} roughness={0.85} /></mesh>
      <mesh geometry={parts.w_hivis} castShadow><meshStandardMaterial color={C.hivis} roughness={0.7} /></mesh>
      <mesh geometry={parts.w_stripe}><meshStandardMaterial color={C.stripe} roughness={0.3} metalness={0.2} /></mesh>
      <mesh geometry={parts.w_skin}><meshStandardMaterial color={C.skin} roughness={0.7} /></mesh>
      <mesh geometry={parts.w_helmet} castShadow><meshStandardMaterial color={C.helmet} roughness={0.3} metalness={0.1} /></mesh>
    </group>
  );
}

/* ---------------- joint flashings ---------------- */

const moduleTop = (m, t, floorHeight) => m.floor * floorHeight + FOUNDATION_H + Math.max(2.2, Math.min(dims(t).h, floorHeight * floorSpan(t)) - 0.06);

/** Cover strips where two modules meet: a flashing over the roof seam and vertical covers on the exposed facade ends. */
function jointParts(modules, types, floorHeight) {
  const rs = modules.map((m) => ({ m, t: types[m.module_type_id] })).filter((x) => x.t).map((x) => ({ ...x, r: rect(x.m, x.t) }));
  const inside = (x, z, f) => rs.some((o) => o.r.f0 <= f && f < o.r.f1 && x > o.r.x0 + 0.01 && x < o.r.x1 - 0.01 && z > o.r.z0 + 0.01 && z < o.r.z1 - 0.01);
  const roof = [], covers = [];
  const wallBand = (f) => { const y0 = f * floorHeight + FOUNDATION_H + BASE_H, y1 = f * floorHeight + FOUNDATION_H + floorHeight - 0.06 - CAP_H; return [y0, y1]; };
  for (let i = 0; i < rs.length; i++) for (let j = i + 1; j < rs.length; j++) {
    const a = rs[i], b = rs[j];
    const f0 = Math.max(a.r.f0, b.r.f0), f1 = Math.min(a.r.f1, b.r.f1);
    if (f1 <= f0) continue;
    // joint along x (east/west neighbours) or along z (north/south neighbours)
    let axis = null, at = 0, s0 = 0, s1 = 0;
    if (Math.abs(a.r.x1 - b.r.x0) < 0.06 || Math.abs(b.r.x1 - a.r.x0) < 0.06) {
      at = Math.abs(a.r.x1 - b.r.x0) < 0.06 ? (a.r.x1 + b.r.x0) / 2 : (b.r.x1 + a.r.x0) / 2;
      s0 = Math.max(a.r.z0, b.r.z0); s1 = Math.min(a.r.z1, b.r.z1);
      if (s1 - s0 >= 0.5) axis = "x";
    }
    if (!axis && (Math.abs(a.r.z1 - b.r.z0) < 0.06 || Math.abs(b.r.z1 - a.r.z0) < 0.06)) {
      at = Math.abs(a.r.z1 - b.r.z0) < 0.06 ? (a.r.z1 + b.r.z0) / 2 : (b.r.z1 + a.r.z0) / 2;
      s0 = Math.max(a.r.x0, b.r.x0); s1 = Math.min(a.r.x1, b.r.x1);
      if (s1 - s0 >= 0.5) axis = "z";
    }
    if (!axis) continue;
    const mid = (s0 + s1) / 2, len = s1 - s0;
    const pt = (s) => (axis === "x" ? [at, s] : [s, at]);
    for (let f = f0; f < f1; f++) {
      const [y0, y1] = wallBand(f);
      for (const [s, dir] of [[s0, -1], [s1, 1]]) {
        const [px, pz] = pt(s + dir * 0.3);
        if (inside(px, pz, f)) continue;
        const [x, z] = pt(s + dir * 0.035);
        covers.push(axis === "x" ? box(0.16, y1 - y0, 0.05, x, (y0 + y1) / 2, z) : box(0.05, y1 - y0, 0.16, x, (y0 + y1) / 2, z));
      }
      // roof flashing only on the topmost shared floor with nothing stacked above
      if (f === f1 - 1 && a.r.f1 === f1 && b.r.f1 === f1) {
        const [px, pz] = pt(mid);
        if (inside(px, pz, f + 1)) continue;
        const top = Math.min(moduleTop(a.m, a.t, floorHeight), moduleTop(b.m, b.t, floorHeight));
        const [x, z] = pt(mid);
        roof.push(axis === "x" ? box(0.26, 0.02, len + 0.1, x, top + 0.01, z) : box(len + 0.1, 0.02, 0.26, x, top + 0.01, z));
      }
    }
  }
  return { roof: merge(roof), covers: merge(covers) };
}

export function Joints({ modules, types, floorHeight }) {
  const parts = useMemo(() => jointParts(modules, types, floorHeight), [modules, types, floorHeight]);
  return (
    <group raycast={() => null}>
      {parts.roof && <mesh geometry={parts.roof} receiveShadow><meshStandardMaterial color={PALETTE.galv} roughness={0.5} metalness={0.5} /></mesh>}
      {parts.covers && <mesh geometry={parts.covers}><meshStandardMaterial color={PALETTE.frame} roughness={0.5} metalness={0.4} /></mesh>}
    </group>
  );
}

/* ---------------- props (vendored CC0 glTF) ---------------- */

class PropBoundary extends React.Component {
  constructor(p) { super(p); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.warn("Rekvisitt kunne ikke lastes", this.props.name, err?.message || err); }
  render() { return this.state.failed ? null : this.props.children; }
}

/** One glTF prop, cloned at several placements ({x, z, yaw}). Shadows on; never interactive. */
function Prop({ name, placements, scale = 1 }) {
  const entry = assets.models[name];
  const { scene } = useGLTF(entry.file);
  return placements.map((p, i) => <Clone key={i} object={scene} position={[p.x, p.y || 0, p.z]} rotation={[0, p.yaw || 0, 0]} scale={scale} castShadow receiveShadow />);
}
function SafeProp(props) {
  if (!assets.models[props.name]) return null;
  return (
    <PropBoundary name={props.name}>
      <Suspense fallback={null}><Prop {...props} /></Suspense>
    </PropBoundary>
  );
}

/** Pallet (procedural) with a stack of cement bags on it. */
function Pallet({ x, z, yaw }) {
  const geo = useMemo(() => merge([
    box(1.2, 0.022, 0.1, 0, 0.133, -0.35), box(1.2, 0.022, 0.1, 0, 0.133, 0), box(1.2, 0.022, 0.1, 0, 0.133, 0.35), box(1.2, 0.022, 0.1, 0, 0.133, -0.17), box(1.2, 0.022, 0.1, 0, 0.133, 0.17),
    box(0.1, 0.08, 0.8, -0.55, 0.082, 0), box(0.1, 0.08, 0.8, 0, 0.082, 0), box(0.1, 0.08, 0.8, 0.55, 0.082, 0),
    box(1.2, 0.022, 0.1, 0, 0.031, -0.35), box(1.2, 0.022, 0.1, 0, 0.031, 0), box(1.2, 0.022, 0.1, 0, 0.031, 0.35),
  ]), []);
  return (
    <group position={[x, 0, z]} rotation={[0, yaw, 0]} raycast={() => null}>
      <mesh geometry={geo} castShadow receiveShadow><meshStandardMaterial color="#b89a6a" roughness={0.9} /></mesh>
    </group>
  );
}

export function Props({ P, b }) {
  const F = FENCE_INSET;
  const z1f = P.z1 - F, cx = (P.x0 + P.x1) / 2;
  const barriers = useMemo(() => [0, 1, 2].map((i) => ({ x: cx + GATE_W / 2 + 1.6 + i * 1.7, z: z1f - 1.15, yaw: 0 })), [cx, z1f]);
  const gen = useMemo(() => ({ x: b.x1 + 1.6, z: b.z0 + 1.2, yaw: -0.4 }), [b.x1, b.z0]);
  const tanks = useMemo(() => [{ x: b.x1 + 2.7, z: b.z0 + 1.0, yaw: 0.3 }, { x: b.x1 + 3.1, z: b.z0 + 1.35, yaw: 1.2 }], [b.x1, b.z0]);
  const pallet = useMemo(() => ({ x: P.x0 + F + 2.2, z: z1f - 3.0, yaw: 0.25 }), [P.x0, z1f]);
  // cement bags stacked 2 × 2 × 3 on the pallet (bag ≈ 0.46 × 0.7 × 0.18 m)
  const bags = useMemo(() => {
    const out = [];
    const c = Math.cos(pallet.yaw), s = Math.sin(pallet.yaw);
    for (let layer = 0; layer < 3; layer++) for (const [lx, lz] of [[-0.27, -0.2], [0.27, -0.2], [-0.27, 0.2], [0.27, 0.2]]) {
      const jitter = ((layer * 7 + lx * 13 + lz * 3) % 1) * 0.02;
      out.push({ x: pallet.x + lx * c + lz * s + jitter, z: pallet.z - lx * s + lz * c, yaw: pallet.yaw + (layer % 2 ? Math.PI / 2 : 0) + jitter, y: 0.144 + layer * 0.18 });
    }
    return out;
  }, [pallet]);
  return (
    <group>
      <SafeProp name="concrete_road_barrier" placements={barriers} />
      <SafeProp name="portable_generator" placements={[gen]} />
      <SafeProp name="propane_tank" placements={tanks} />
      <Pallet {...pallet} />
      <SafeProp name="cement_bag" placements={bags} />
    </group>
  );
}

/* ---------------- natural terrain beyond the pad ---------------- */

/**
 * Ground outside the rig pad in Grid mode (3D only): a 900 m disc with an aerial moss/rock terrain photo (15 m per
 * repeat, UVs in metres) that fades into the fog at the horizon. Falls back to a flat tone until the map is ready.
 */
export function Terrain({ center, night = false }) {
  const photo = usePhotoMaps();
  const pm = photo?.terreng;
  const geo = useMemo(() => {
    const R = 900;
    const g = new THREE.CircleGeometry(R, 96);
    g.rotateX(-Math.PI / 2);
    const uv = g.attributes.uv; // 0..1 across the diameter → metres
    for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * 2 * R, uv.getY(i) * 2 * R);
    uv.needsUpdate = true;
    return g;
  }, []);
  return (
    <mesh geometry={geo} position={[center[0], -0.03, center[1]]} receiveShadow raycast={() => null}>
      {pm ? (
        <meshStandardMaterial map={pm.map} normalMap={pm.normalMap} normalScale={[0.55, 0.55]} color={night ? "#5d615b" : "#c3c2b7"} roughness={1} metalness={0} />
      ) : (
        <meshStandardMaterial color={night ? "#4a5049" : "#8a9084"} roughness={1} metalness={0} />
      )}
    </mesh>
  );
}

/* ---------------- composition ---------------- */

/**
 * `env`: { ground: "asfalt" | "grus" | "ingen", fence: bool, scale: bool, joints: bool }. Joint flashings are 3D-only so
 * the plan view keeps its crisp module outlines; the pad fades in 2D so the metric grid stays readable.
 */
export function Surroundings({ modules, types, floorHeight, bounds: b, env, view }) {
  const P = useMemo(() => padRect(b), [b.x0, b.x1, b.z0, b.z1]);
  if (!modules.length || !env) return null;
  const is3D = view === "3D";
  return (
    <group>
      {env.ground && env.ground !== "ingen" && GROUND_SPEC[env.ground] && <RigPad P={P} kind={env.ground} faded={!is3D} />}
      {env.fence && <Fence P={P} />}
      {env.scale && <ScaleAssets P={P} b={b} />}
      {env.props !== false && <Props P={P} b={b} />}
      {env.joints && is3D && <Joints modules={modules} types={types} floorHeight={floorHeight} />}
    </group>
  );
}
